import { QueryKey, useMutation, MutateOptions, MutationFunction, QueryFunctionContext } from '@tanstack/react-query'
import { Success, Fail, ExtendedError } from 'store/types'
import { FixMeAny } from 'types/global'

export type AppMutateOptions<Response = FixMeAny, Payload = FixMeAny> = MutateOptions<
  Response,
  ExtendedError | undefined,
  Payload,
  QueryFunctionContext
>

type MutateSuccess<T> = {
  isSuccess: true
  isError: false
  data: T
  error?: never
}

type MutateError = {
  isSuccess: false
  isError: true
  data?: never
  error: ExtendedError
}

export const useAppMutation = <Response, Payload>(
  queryKeys: QueryKey,
  queryFn: MutationFunction<Fail | Success<Response>, Payload>,
  options?: MutateOptions<Response, ExtendedError | undefined, Payload, QueryFunctionContext | undefined>,
) => {
  const mutation = useMutation<Fail | Success<Response>, ExtendedError, Payload, QueryFunctionContext>(
    queryKeys,
    queryFn,
    options && {
      ...options,
      onSuccess: (data, variable, context) => {
        if (data.status === 'SUCCESS') {
          return options?.onSuccess?.(data.data, variable, context)
        }
        return options?.onError?.(data.error, variable, context)
      },
      onError: (err, variable, context) => {
        options?.onError?.(err, variable, context)
      },
      onSettled: (data, error, variable, context) => {
        if (data?.status === 'SUCCESS') {
          return options?.onSettled?.(data?.data, error, variable, context)
        }
        return options?.onSettled?.(undefined, error, variable, context)
      },
    },
  )

  return {
    ...mutation,
    mutate: (payload: Payload, opts?: MutateOptions<Response, ExtendedError, Payload, QueryFunctionContext>) =>
      mutation.mutate(payload, {
        ...opts,
        onSuccess: (data, variable, context) => {
          if (data.status === 'SUCCESS') {
            return opts?.onSuccess?.(data.data, variable, context)
          }
          if (data.status === 'FAIL' && data.error) {
            return opts?.onError?.(data.error, variable, context)
          }
          opts?.onError?.(
            {
              code: -1,
              name: 'UnknownError',
              message: 'Unknown error',
            },
            variable,
            context,
          )
        },
        onError: (err, variable, context) => {
          opts?.onError?.(err, variable, context)
        },
        onSettled: (data, error, variable, context) => {
          if (data?.status === 'SUCCESS') {
            return opts?.onSettled?.(data?.data, error, variable, context)
          }
          opts?.onSettled?.(undefined, error, variable, context)
        },
      }),
    mutateAsync: async (
      payload: Payload,
      opts?: MutateOptions<Response, ExtendedError, Payload, QueryFunctionContext>,
    ): Promise<MutateSuccess<Response> | MutateError> => {
      try {
        const response = await mutation.mutateAsync(payload, {
          ...opts,
          onSuccess: (data, variable, context) => {
            if (data.status === 'SUCCESS') {
              return opts?.onSuccess?.(data.data, variable, context)
            }
            if (data.status === 'FAIL' && data.error) {
              return opts?.onError?.(data.error, variable, context)
            }
          },
          onError: (err, variable, context) => {
            opts?.onError?.(err, variable, context)
          },
          onSettled: (data, error, variable, context) => {
            if (data?.status === 'SUCCESS') {
              return opts?.onSettled?.(data?.data, error, variable, context)
            }
            return opts?.onSettled?.(undefined, error, variable, context)
          },
        })

        if (response.status === 'SUCCESS') {
          return { isSuccess: true, isError: false, data: response.data }
        }
        if (response.status === 'FAIL' && response.error) {
          return { isSuccess: false, isError: true, error: response.error }
        }
        return {
          isSuccess: false,
          isError: true,
          data: undefined,
          error: {
            code: -1,
            name: 'UnknownError',
            message: 'Unknown error',
          },
        }
      } catch (e) {
        return { isSuccess: false, isError: true, error: e as ExtendedError }
      }
    },
  }
}

export default useAppMutation
